import React, { useEffect, useReducer } from 'react';
import { dataFetchReducer } from '../../data/data';
import { parseAutocompleteData } from '../../data/parseAutocompleteData';
import FastbetCartContext from '../../data/fastbet/FastbetCartContext';
import { filterSearchResults } from '../../data/utils';
import Loader from '../../common/Loader';
import EventSearch from './EventSearch';
import { usePrematchData } from '../PrematchDataFetcher';
import NoResults from './NoResults';

export default function ResultsFetcher(props) {
    const fastbetContext = React.useContext(FastbetCartContext);
    const prematchData = usePrematchData();
    const search = props.search;

    const [state, dispatch] = useReducer(dataFetchReducer, {
        isLoading: true,
        isError: false,
    });

    useEffect(() => {
        return () => window.dispatchEvent(new CustomEvent('reset-search'));
    }, []);

    useEffect(() => {
        if (search.length >2 && state.data == null) {
        	let source = props.isLive? fastbetContext.getRawDataLive() : fastbetContext.getRawDataPrematch();
        	if (source != null) {
        		const parsedData = parseAutocompleteData(source, props.isLive);
        		dispatch({ type: 'FETCH_SUCCESS', payload: parsedData });        		
        	} else {
                dispatch({ type: 'FETCH_FAILURE' });
        	}
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, state.data, props.isLive]);

    if (state.isLoading || state.isError) {
        return (
            <Loader error={state.error} />
        );
    }

    let results = [];
    if (search.length > 2) {
        results = filterSearchResults(state.data, search, 'MATCH', function(match) {
            return match.desc;
        }, true);
    
        results.sort((a, b) => {
            let sport = prematchData.sportById[a.sportRef];
            let category = sport?.categoryById[a.categoryRef];
            let tournament = category?.tournamentById[a.tournamentRef];
            const [aSportRanking, aCategoryRanking, aTournamentRanking] = [sport.ranking, category.ranking, tournament.ranking];
           
            sport = prematchData.sportById[b.sportRef];
            category = sport?.categoryById[b.categoryRef];
            tournament = category?.tournamentById[b.tournamentRef];
            const [bSportRanking, bCategoryRanking, bTournamentRanking] = [sport?.ranking, category?.ranking, tournament?.ranking];
    
            if (aSportRanking !== bSportRanking) {
                return aSportRanking - bSportRanking;
            }
            if (aCategoryRanking !== bCategoryRanking) {
                return aCategoryRanking - bCategoryRanking;
            }
            if (aTournamentRanking !== bTournamentRanking) {
                return aTournamentRanking - bTournamentRanking;
            }
            return a.timestampDB.localeCompare(b.timestampDB);
        });
    }

    if (search.length === 0 || results.length === 0) {
        return (
            <NoResults />
        )
    }
    
    const sports = [];
    const sportById = new Map();
    results.forEach((r, i) => {
        // Create sport
        if (!sportById.has(r.sportRef)) {
            const sport = {
                id: r.sportRef,
                desc: r.sportDesc,
                matches: [],
                matchesMap: new Map()
            };
            sports.push(sport);
            sportById.set(r.sportRef, sport);
        }
        
        const sport = sportById.get(r.sportRef);
        const matchString = r.matchingToken;
        // Create match group
        if (!sport.matchesMap.has(matchString)) {
            const match = {
                id: matchString,
                tournaments: [],
                tournamentById: new Map()
            };
            sport.matches.push(match);
            sport.matchesMap.set(matchString, match);
        }
        
        const match = sport.matchesMap.get(matchString);
        // Create tournament
        const uniqueId = `${r.tournamentRef}_${r.openAggregata}`;
        if (!match.tournamentById.has(uniqueId)) {
            const tournament = {
                id: r.tournamentRef,
                desc: r.tournamentDesc,
                dateById: new Map(),
                dates: [],
            };
            match.tournaments.push(tournament);
            match.tournamentById.set(uniqueId, tournament);
        }

        const timestamp = r.timestampDB;
        const tournament = match.tournamentById.get(uniqueId);
        if (!tournament.dateById.has(timestamp)) {
            tournament.dates.push(timestamp);
            tournament.dateById.set(timestamp, []);
        }
        tournament.dateById.get(timestamp).push(r);
        r.bets = r.bets.slice(0, 1);
    });

    // If prematch, sort sports by ranking
    if (!props.isLive) {
        try {
            sports.sort((a, b) => {
                const rankingA = props.data.sportById[a.id].ranking;
                const rankingB = props.data.sportById[b.id].ranking;
                return rankingA - rankingB;
            });
        } catch(e) {
            console.error(e);
        }
    }

    return <EventSearch key={search} search={search} data={{results, sports}} />
}
